import Sortable from "sortablejs";
import { insertNodeAt, camelize, console, removeNode } from "./utils/helper";
import {
  defineComponent,
  h,
  nextTick,
  reactive,
  toRefs,
  computed,
  onBeforeUnmount,
  onMounted,
  watch,
  getCurrentInstance,
  toRaw
} from 'vue';

function buildAttribute(object, propName, value) {
  if (value === undefined) {
    return object;
  }
  object = object || {};
  object[propName] = value;
  return object;
}

function computeVmIndex(vnodes, element) {
  // Vue 3 compatible version - handle fragments and extract DOM elements properly
  const domElements = [];

  vnodes.forEach(vnode => {
    if (vnode.type === Symbol.for('v-fgt') || vnode.type?.toString?.() === 'Symbol(v-fgt)') {
      // Handle Vue 3 fragments - extract children
      if (vnode.children && Array.isArray(vnode.children)) {
        vnode.children.forEach(child => {
          domElements.push(child.el || child.elm);
        });
      }
    } else {
      // Regular vnode
      domElements.push(vnode.el || vnode.elm);
    }
  });

  const index = domElements.indexOf(element);
  return index;
}

function computeIndexes(slots, children, isTransition, footerOffset) {
  if (!slots) {
    return [];
  }

  const elmFromNodes = slots.map(elt => elt.el || elt.elm);
  const footerIndex = children.length - footerOffset;
  const rawIndexes = [...children].map((elt, idx) =>
    idx >= footerIndex ? elmFromNodes.length : elmFromNodes.indexOf(elt)
  );
  return isTransition ? rawIndexes.filter(ind => ind !== -1) : rawIndexes;
}

function safeEmit(instance, eventName, eventData) {
  // Handle null instance
  if (!instance) {
    return;
  }

  // Vue 3 style - use nextTick for proper timing
  nextTick(() => {
    if (instance.emit) {
      instance.emit(eventName.toLowerCase(), eventData);
    } else if (instance.proxy && instance.proxy.$emit) {
      instance.proxy.$emit(eventName.toLowerCase(), eventData);
    } else if (instance.$emit) {
      // Vue 2 compatibility
      instance.$emit(eventName.toLowerCase(), eventData);
    } else {
      console.warn('No emit method found on instance for event:', eventName);
    }
  });
}


function isTransitionName(name) {
  return ["transition-group", "TransitionGroup"].includes(name);
}

function isTransition(slots) {
  if (!slots || slots.length !== 1) {
    return false;
  }

  const slotContent = slots[0];

  if (slotContent.componentOptions) {
    return isTransitionName(slotContent.componentOptions.tag);
  }

  if (slotContent.type) {
    return isTransitionName(slotContent.type.name || slotContent.type);
  }

  return false;
}

function getSlot(slots, scopedSlots, key) {
  if (slots && typeof slots === 'object') {
    if (slots[key]) {
      return typeof slots[key] === 'function' ? slots[key]() : slots[key];
    }
  }

  if (scopedSlots && scopedSlots[key]) {
    return scopedSlots[key]();
  }

  return null;
}

function computeChildrenAndOffsets(children, slots, scopedSlots) {
  let headerOffset = 0;
  let footerOffset = 0;
  const header = getSlot(slots, scopedSlots, "header");
  if (header) {
    headerOffset = header.length;
    children = children ? [...header, ...children] : [...header];
  }
  const footer = getSlot(slots, scopedSlots, "footer");
  if (footer) {
    footerOffset = footer.length;
    children = children ? [...children, ...footer] : [...footer];
  }
  return { children, headerOffset, footerOffset };
}

function getComponentAttributes(attrs, componentData) {
  let attributes = null;
  const update = (name, value) => {
    attributes = buildAttribute(attributes, name, value);
  };
  const filteredAttrs = Object.keys(attrs)
    .filter(key => key === "id" || key.startsWith("data-"))
    .reduce((res, key) => {
      res[key] = attrs[key];
      return res;
    }, {});
  update("attrs", filteredAttrs);

  if (!componentData) {
    return attributes;
  }
  const { on, props, attrs: componentDataAttrs } = componentData;
  update("on", on);
  update("props", props);
  Object.assign(attributes.attrs || {}, componentDataAttrs || {});
  return attributes;
}

const eventsToEmit = ["Choose", "Unchoose", "Sort", "Filter", "Clone"];
const readonlyProperties = ["Move", "Start", "Add", "Remove", "Update", "End", ...eventsToEmit].map(
  evt => "on" + evt
);
let draggingElement = null;

export default defineComponent({
  name: "VueDraggable",
  inheritAttrs: false,
  props: {
    options: {
      type: Object,
      required: false,
      default: null
    },
    list: {
      type: Array,
      required: false,
      default: null
    },
    value: {
      type: Array,
      required: false,
      default: null
    },
    modelValue: {
      type: Array,
      required: false,
      default: null
    },
    itemKey: {
      type: String,
      required: false,
      default: null
    },
    noTransitionOnDrag: {
      type: Boolean,
      default: false
    },
    clone: {
      type: Function,
      default: (original) => original
    },
    element: {
      type: String,
      default: "div"
    },
    tag: {
      type: String,
      default: null
    },
    move: {
      type: Function,
      default: null
    },
    componentData: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: [
    'update:modelValue',
    'input',
    'change',
    ...eventsToEmit.map(s => s.toLowerCase()),
    'start',
    'add',
    'remove',
    'update',
    'end',
    'choose',
    'unchoose',
    'sort',
    'filter',
    'clone'
  ],
  setup(props, { emit, slots, attrs }) {
    const state = reactive({
      transitionMode: false,
      noneFunctionalComponentMode: false,
      headerOffset: 0,
      footerOffset: 0,
      _sortable: null,
      visibleIndexes: [],
      context: null
    });

    const rootContainer = computed(() => {
      return state.transitionMode ? getCurrentInstance().vnode.el.children[0] : getCurrentInstance().vnode.el;
    });

    const realList = computed(() => {
      return props.list ? props.list : (props.modelValue || props.value);
    });

    const getTag = () => {
      return props.tag || props.element || "div";
    };

    const getIsFunctional = () => {
      const { tag } = props;
      if (!tag || tag === 'div') {
        return false;
      }
      return true;
    };

    function getChildrenNodes() {
      if (state.noneFunctionalComponentMode) {
        const nodes = slots.default ? (typeof slots.default === 'function' ? slots.default() : slots.default) : [];
        return nodes;
      }
      const rawNodes = slots.default ? (typeof slots.default === 'function' ? slots.default() : slots.default) : [];
      return rawNodes;
    }

    function computeDraggableIndexes() {
      const children = getChildrenNodes();
      if (children.length === 0) {
        return [];
      }
      state.noneFunctionalComponentMode = getTag().toLowerCase() !== rootContainer.value.nodeName.toLowerCase() && !getIsFunctional();
      const indexes = computeIndexes(
        children,
        rootContainer.value.children,
        state.transitionMode,
        state.footerOffset
      );
      return indexes;
    }

    /**
* Maps a DOM element to its corresponding data item in the reactive list.
* This function uses multiple fallback strategies to ensure robust element identification
* regardless of content type (numbers, text, symbols, duplicates, etc.).
*
* @param {HTMLElement} htmlElt - The DOM element to map to data
* @returns {Object|null} - Object with {index, element} or null if not found
*/
    function getUnderlyingVm(htmlElt) {
      const children = getChildrenNodes();
      let index = computeVmIndex(children, htmlElt);

      // If primary Vue-based mapping failed, use robust DOM-based fallback strategies
      if (index === -1) {
        const container = rootContainer.value;
        const tagName = htmlElt.tagName.toLowerCase();

        // Get all container children matching the dragged element's tag type
        const containerChildren = Array.from(container.children).filter(child =>
          child.tagName.toLowerCase() === tagName
        );

        // Strategy 1: Direct DOM position matching
        // Most reliable method - finds exact position in filtered children array
        const draggedPosition = containerChildren.indexOf(htmlElt);

        if (draggedPosition !== -1) {
          // Ensure position is within bounds of our data array
          if (draggedPosition < realList.value.length) {
            index = draggedPosition;
          } else {
            // Handle edge case where DOM has more elements than data array
            // Clamp to valid range to prevent array out-of-bounds errors
            index = Math.min(draggedPosition, realList.value.length - 1);
          }
        } else {
          // Strategy 2: Sibling traversal fallback
          // Manually traverse DOM siblings to find element position
          // Useful when direct indexOf fails due to DOM manipulations
          let currentElement = container.firstElementChild;
          let siblingIndex = 0;

          while (currentElement && currentElement !== htmlElt) {
            if (currentElement.tagName.toLowerCase() === tagName) {
              siblingIndex++;
            }
            currentElement = currentElement.nextElementSibling;
          }

          if (currentElement === htmlElt && siblingIndex < realList.value.length) {
            index = siblingIndex;
          } else {
            // Strategy 3: Safe bounds-based fallback
            // Last resort to prevent crashes - uses array bounds to determine safe index
            // Ensures we always return a valid index within data array bounds
            index = Math.max(0, Math.min(containerChildren.length - 1, realList.value.length - 1));
          }
        }
      }

      // Final validation to ensure index is valid
      if (index === -1 || index >= realList.value.length) {
        return null;
      }

      const element = realList.value[index];
      return { index, element };
    }

    function getUnderlyingPotencialDraggableComponent(el) {
      let vue = el.__vue__ || el.__vueParentComponent;

      if (!vue) {
        return el;
      }

      if (el.__vueParentComponent) {
        vue = el.__vueParentComponent.ctx;
      }

      if (vue.$options && isTransitionName(vue.$options._componentTag || vue.$options.name)) {
        return vue.$parent;
      }

      if ((!vue.realList) && vue.$children && vue.$children.length === 1 && vue.$children[0].realList) {
        return vue.$children[0];
      }

      return vue;
    }

    function emitChanges(evt) {
      const listCopy = [...realList.value];
      nextTick(() => {
        emit("update:modelValue", listCopy);
        emit("input", listCopy);
      });
      emit("change", evt);
    }

    function alterList(onList) {
      if (props.list) {
        onList(props.list);
        return;
      }
      const newList = [...realList.value];
      onList(newList);

      // Immediately update the realList to reflect changes
      realList.value.splice(0, realList.value.length, ...newList);

      if (props.modelValue !== undefined) {
        emit("update:modelValue", newList);
      }
      emit("input", newList);
    }

    function spliceList() {
      const spliceList = list => list.splice(...arguments);
      alterList(spliceList);
    }

    function updatePosition(oldIndex, newIndex) {
      const updatePosition = list => {
        const movedItem = list.splice(oldIndex, 1)[0];
        list.splice(newIndex, 0, movedItem);
        return list;
      };
      alterList(updatePosition);
    }

    function getRelatedContextFromMoveEvent({ to, related }) {
      const component = getUnderlyingPotencialDraggableComponent(to);
      if (!component) {
        return { component };
      }

      let list;
      let componentContext;

      if (component.__draggable_context__) {
        componentContext = component.__draggable_context__;
        list = componentContext.list;
      } else {
        list = component.realList || [];
        componentContext = {
          component,
          list
        };
      }

      const context = { list, component };

      if (to !== related && list && component.getUnderlyingVm) {
        const destination = component.getUnderlyingVm(related);
        if (destination) {
          return { ...destination, ...context };
        }
      }

      return context;
    }

    function getVmIndex(domIndex) {
      // Simple bounds checking - domIndex should correspond directly to realList index
      if (domIndex < 0) {
        return 0;
      }

      if (domIndex >= realList.value.length) {
        return realList.value.length - 1;
      }

      return domIndex;
    }

    function getComponent() {
      if (slots.default) {
        if (typeof slots.default === 'function') {
          const slots = slots.default();
          return slots[0]?.component || slots[0]?.componentInstance;
        }
        return slots.default[0]?.componentInstance;
      }
      return null;
    }

    function resetTransitionData(index) {
      if (!props.noTransitionOnDrag || !state.transitionMode) {
        return;
      }

      const nodes = getChildrenNodes();
      if (!nodes || !nodes[index]) return;

      if (nodes[index].data) {
        nodes[index].data = null;
      } else if (nodes[index].props) {
        nodes[index].props = null;
      }

      const transitionContainer = getComponent();
      if (transitionContainer) {
        if (transitionContainer.children) {
          transitionContainer.children = [];
        }
        if (transitionContainer.kept) {
          transitionContainer.kept = undefined;
        }
      }
    }

    function onDragStart(evt) {
      state.context = getUnderlyingVm(evt.item);
      if (state.context) {
        evt.item._underlying_vm_ = props.clone(state.context.element);
        draggingElement = evt.item;
      }
    }

    function onDragAdd(evt) {
      const element = evt.item._underlying_vm_;
      if (element === undefined) {
        return;
      }

      removeNode(evt.item);
      const newIndex = getVmIndex(evt.newIndex);

      spliceList(newIndex, 0, element);
      computeDraggableIndexes();

      const added = { element, newIndex };
      emitChanges({ added });
    }

    function onDragRemove(evt) {
      insertNodeAt(rootContainer.value, evt.item, evt.oldIndex);

      if (evt.pullMode === "clone") {
        removeNode(evt.clone);
        return;
      }

      if (!state.context) {
        return;
      }

      const oldIndex = state.context.index;
      spliceList(oldIndex, 1);

      const removed = { element: state.context.element, oldIndex };
      resetTransitionData(oldIndex);
      emitChanges({ removed });
    }

    function onDragUpdate(evt) {
      removeNode(evt.item);
      insertNodeAt(evt.from, evt.item, evt.oldIndex);

      if (!state.context) {
        return;
      }

      const oldIndex = state.context.index;
      const newIndex = getVmIndex(evt.newIndex);

      updatePosition(oldIndex, newIndex);

      const moved = { element: state.context.element, oldIndex, newIndex };
      emitChanges({ moved });
    }

    function onDragEnd(evt) {
      computeDraggableIndexes();
      draggingElement = null;
    }

    function onDragMove(evt, originalEvent) {
      const onMove = props.move;
      if (!onMove || !realList.value) {
        return true;
      }

      const relatedContext = getRelatedContextFromMoveEvent(evt);
      const draggedContext = state.context;

      if (!draggedContext) {
        return true;
      }

      const futureIndex = computeFutureIndex(relatedContext, evt);

      Object.assign(draggedContext, {
        futureIndex
      });

      const sendEvt = {
        ...evt,
        relatedContext,
        draggedContext
      };

      return onMove(sendEvt, originalEvent);
    }

    function computeFutureIndex(relatedContext, evt) {
      if (!relatedContext.element) {
        return 0;
      }

      const domChildren = [...evt.to.children].filter(
        el => el.style["display"] !== "none"
      );

      const currentDOMIndex = domChildren.indexOf(evt.related);
      const component = relatedContext.component;

      if (!component || !component.getVmIndex) {
        return currentDOMIndex;
      }

      const currentIndex = component.getVmIndex(currentDOMIndex);
      return currentIndex + 1;
    }

    function clone(element) {
      if (typeof props.clone === 'function') {
        return props.clone(element);
      }
      return element;
    }

    function updateOptions(newOptionValue) {
      for (var property in newOptionValue) {
        const value = camelize(property);
        if (readonlyProperties.indexOf(value) === -1) {
          state._sortable.option(value, newOptionValue[property]);
        }
      }
    }

    onMounted(() => {
      const defaultSlot = slots.default;
      const defaultSlotContent = defaultSlot ?
        (typeof defaultSlot === 'function' ? defaultSlot() : defaultSlot) :
        [];
      state.transitionMode = isTransition(defaultSlotContent);
      const computedSlots = slots;

      const { children, headerOffset, footerOffset } = computeChildrenAndOffsets(
        getChildrenNodes(),
        computedSlots
      );

      state.headerOffset = headerOffset;
      state.footerOffset = footerOffset;

      state.noneFunctionalComponentMode = getTag().toLowerCase() !== getCurrentInstance().vnode.el.nodeName.toLowerCase() &&
        !getIsFunctional();

      if (state.noneFunctionalComponentMode && state.transitionMode) {
        throw new Error(
          `Transition-group inside component is not supported. Please alter tag value or remove transition-group. Current tag value: ${getTag()}`
        );
      }

      const optionsAdded = {};

      // Directly assign drag event handlers instead of using delegateAndEmit
      optionsAdded.onStart = (evt) => {
        onDragStart(evt);
        emit("start", evt);
      };

      optionsAdded.onAdd = (evt) => {
        onDragAdd(evt);
        emit("add", evt);
      };

      optionsAdded.onRemove = (evt) => {
        onDragRemove(evt);
        emit("remove", evt);
      };

      optionsAdded.onUpdate = (evt) => {
        onDragUpdate(evt);
        emit("update", evt);
      };

      optionsAdded.onEnd = (evt) => {
        onDragEnd(evt);
        emit("end", evt);
      };

      eventsToEmit.forEach(elt => {
        optionsAdded["on" + elt] = (event) => emit(elt.toLowerCase(), event);
      });

      const attributes = Object.keys(attrs).reduce((res, key) => {
        res[camelize(key)] = attrs[key];
        return res;
      }, {});

      const options = Object.assign({}, props.options, attributes, optionsAdded, {
        onMove: (evt, originalEvent) => {
          return onDragMove(evt, originalEvent);
        }
      });

      !("draggable" in options) && (options.draggable = ">*");

      state._sortable = new Sortable(rootContainer.value, options);

      computeDraggableIndexes();

      if (props.list || props.modelValue) {
        const draggable = state._sortable;
        draggable.sort(getChildrenNodes().map(node => node.el));
      }


    });

    onBeforeUnmount(() => {
      if (state._sortable !== undefined) state._sortable.destroy();
    });

    watch(() => props.options, (newOptionValue) => {
      updateOptions(newOptionValue);
    }, { deep: true });

    watch(() => props.list, () => {
      const draggable = state._sortable;
      if (draggable) draggable.sort(getChildrenNodes().map(node => node.el));
    }, { deep: true });

    watch(() => props.modelValue, () => {
      const draggable = state._sortable;
      if (draggable) draggable.sort(getChildrenNodes().map(node => node.el));
    }, { deep: true });

    const componentInstance = {
      ...toRefs(state),
      getTag,
      getIsFunctional,
      getChildrenNodes,
      computeDraggableIndexes,
      getUnderlyingVm,
      getUnderlyingPotencialDraggableComponent,
      emitChanges,
      alterList,
      spliceList,
      updatePosition,
      getRelatedContextFromMoveEvent,
      getVmIndex,
      getComponent,
      resetTransitionData,
      onDragStart,
      onDragAdd,
      onDragRemove,
      onDragUpdate,
      onDragEnd,
      onDragMove,
      computeFutureIndex,
      clone,
      updateOptions
    };

    const currentInstance = getCurrentInstance();
    Object.keys(componentInstance).forEach(key => {
      if (typeof componentInstance[key] === 'function') {
        currentInstance.ctx[key] = componentInstance[key];
      } else if (key !== '_value' && key !== '__v_isRef' && key !== '__v_isShallow') {
        try {
          currentInstance.ctx[key] = toRaw(componentInstance[key]);
        } catch (e) {
          currentInstance.ctx[key] = componentInstance[key];
        }
      }
    });

    return componentInstance;
  },
  render() {
    const slots = this.$slots;
    const attrs = this.$attrs;
    const tag = this.getTag();
    const children = slots.default ? (typeof slots.default === 'function' ? slots.default() : slots.default) : [];

    const { headerOffset, footerOffset } = this;
    const { children: processedChildren } = computeChildrenAndOffsets(
      children,
      slots
    );

    const attributes = getComponentAttributes(attrs, this.componentData);

    if (this.itemKey && children && children.length > 0) {
      const defaultContent = children;
      return h(tag, attributes, defaultContent);
    } else {
      return h(tag, attributes, processedChildren);
    }
  }
});
